// Fold only settled, read-only exploration. Keep the original receipts and
// chronology intact. Repeated failures and incomplete searches fold separately; active calls,
// successful mutations and narration always remain visible.
const EXPLORATION = new Set(['project_read', 'project_list', 'project_search', 'project_patch_status', 'process_list',
  'git_status', 'git_diff', 'document_info', 'document_extract', 'document_inspect', 'read_collection_file']);

function summary(rows) {
  const files = new Set();
  let searches = 0, folders = 0;
  for (const { line } of rows) {
    const tool = line.part.tool, input = line.part.state?.input || {}, path = input.path;
    const filenameQuery = tool === 'project_list' && typeof input.query === 'string'
      && input.query.length >= 1 && input.query.length <= 200 && !/[\u0000-\u001f\u007f]/.test(input.query);
    if (tool === 'project_search' || filenameQuery) searches++;
    else if (tool === 'project_list') folders++;
    else if (tool === 'project_worktree' && input.operation === 'read' && path) files.add(`worktree:${input.worktreeId}/${path}`);
    else if (tool === 'read_collection_file' && input.fileId) files.add(`collection:${line.part.state.metadata?.collectionId}/${input.fileId}`);
    else if (['project_read', 'document_extract', 'document_inspect'].includes(tool) && path) files.add(path);
  }
  const labels = [];
  if (files.size) labels.push(`Read ${files.size} ${files.size === 1 ? 'file' : 'files'}`);
  if (searches) labels.push(`${searches} ${searches === 1 ? 'search' : 'searches'}`);
  if (folders) labels.push(`${folders} folder ${folders === 1 ? 'listing' : 'listings'}`);
  return labels.join(' · ') || 'Explored the project';
}

function failureSummary(rows, tool) {
  const title = tool === 'process_start' ? 'Command attempts failed' : 'Repeated action failures';
  const state = rows[rows.length - 1].line.part?.state;
  let error = state?.metadata?.error || state?.error;
  // Native preflight failures may live in the completed tool's JSON output.
  if (!error && typeof state?.output === 'string' && state.output.length <= 8192) {
    try { error = JSON.parse(state.output)?.error; } catch (_) { /* Keep the compact title. */ }
  }
  if (typeof error !== 'string' || /^[\s]*[\[{]/.test(error)) return title;
  const text = error.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
  return text ? `${title}: ${text.slice(0, 120)}${text.length > 120 ? '…' : ''}` : title;
}

export function groupCompletedWorkActivity(keyed) {
  const result = [];
  let pending = [], pendingFailure = null, pendingIncomplete = null;
  const flush = () => {
    if (pendingFailure && pending.length >= 2) result.push({ key: `failures:${pending[0].key}`,
      lines: pending.map(row => row.line), summary: failureSummary(pending, pendingFailure),
      collapseFrom: 2, countLabel: `${pending.length} attempts` });
    else if (pendingIncomplete && pending.length >= 2) result.push({ key: `incomplete:${pending[0].key}`,
      lines: pending.map(row => row.line),
      summary: pendingIncomplete === 'search' ? 'Searches incomplete; narrow the scope' : 'Folder listings incomplete; narrow the folder',
      collapseFrom: 2, countLabel: `${pending.length} attempts` });
    else if (!pendingFailure && !pendingIncomplete && pending.length >= 3) result.push({ key: `exploration:${pending[0].key}`,
      lines: pending.map(row => row.line), summary: summary(pending) });
    else result.push(...pending);
    pending = []; pendingFailure = null; pendingIncomplete = null;
  };
  for (const row of keyed) {
    const failure = row.line.kind === 'tool' && ['failed', 'error', 'completed'].includes(row.line.status)
      && (row.line.isError || ['failed', 'error'].includes(row.line.status)) && row.line.part?.tool;
    if (failure) {
      if (pending.length && pendingFailure !== failure) flush();
      pendingFailure = failure; pending.push(row); continue;
    }
    if (pendingFailure) flush();
    const tool = row.line.part?.tool, query = row.line.part?.state?.input?.query;
    const incomplete = row.line.kind === 'tool' && row.line.status === 'completed' && row.line.isIncomplete
      && !row.line.isError && ['project_list', 'project_search'].includes(tool)
      ? tool === 'project_search' || typeof query === 'string' && query.length >= 1 && query.length <= 200 && !/[\u0000-\u001f\u007f]/.test(query)
        ? 'search' : 'listing' : null;
    if (incomplete) {
      if (pending.length && pendingIncomplete !== incomplete) flush();
      pendingIncomplete = incomplete; pending.push(row); continue;
    }
    if (pendingIncomplete) flush();
    if (row.line.kind === 'tool' && row.line.status === 'completed'
        && !row.line.isError && !row.line.isIncomplete && (EXPLORATION.has(row.line.part?.tool)
          || row.line.part?.tool === 'project_worktree' && ['list', 'read', 'review', 'verification_status'].includes(row.line.part.state?.input?.operation)
          || row.line.part?.tool === 'project_servers' && ['list', 'listeners', 'status'].includes(row.line.part.state?.input?.operation))) pending.push(row);
    else { flush(); result.push(row); }
  }
  flush();
  return result;
}

// Mac Work's read/edit/command loop is one activity trail. A tool changing type
// must not unfold it. Narration and unresolved actions remain in chronological
// order outside each fold; every settled receipt stays inside in its original order.
export function groupSettledWorkActivity(keyed) {
  const result = [];
  // A launch receipt is immutable and can still say "running" after a later
  // status receipt reports exit. Resolve by owned process ID, never by caption
  // or output text, without rewriting either receipt.
  const processes = new Map();
  for (const { line } of keyed) {
    const metadata = line.part?.state?.metadata;
    for (const process of [metadata?.process, ...(Array.isArray(metadata?.processes) ? metadata.processes : [])]) {
      if (process?.processId && process.state) processes.set(process.processId, process.state);
    }
  }
  let pending = [];
  const flush = () => {
    if (pending.length < 2) result.push(...pending);
    else {
      const legacy = groupCompletedWorkActivity(pending);
      const uniform = legacy.length === 1 && legacy[0].lines;
      let label = uniform ? legacy[0].summary : summary(pending.filter(({ line }) => line.status === 'completed' && !line.isError && !line.isIncomplete));
      if (!uniform) {
        const edits = pending.filter(({ line }) => ['project_write', 'project_edit', 'project_patch', 'document_edit'].includes(line.part?.tool)).length;
        const commands = pending.filter(({ line }) => line.part?.tool === 'process_start').length;
        const failed = pending.filter(({ line }) => line.isError || ['failed', 'error'].includes(line.status)).length;
        const incomplete = pending.filter(({ line }) => line.isIncomplete && !line.isError).length;
        const cancelled = pending.filter(({ line }) => line.status === 'cancelled').length;
        const labels = label === 'Explored the project' ? [] : [label];
        if (edits) labels.push(`${edits} ${edits === 1 ? 'edit' : 'edits'}`);
        if (commands) labels.push(`${commands} ${commands === 1 ? 'command' : 'commands'}`);
        if (failed) labels.push(`${failed} failed`);
        if (incomplete) labels.push(`${incomplete} incomplete`);
        if (cancelled) labels.push(`${cancelled} cancelled`);
        label = labels.join(' · ') || 'Worked in the project';
      }
      result.push({ key: `activity:${pending[0].key}`, lines: pending.map(row => row.line),
        summary: label, collapseFrom: 2,
        ...(uniform && legacy[0].countLabel ? { countLabel: legacy[0].countLabel } : {}) });
    }
    pending = [];
  };
  for (const row of keyed) {
    const line = row.line;
    const process = line.part?.state?.metadata?.process;
    const processState = processes.get(process?.processId) || process?.state;
    const activeCommand = ['starting', 'running', 'stopping'].includes(line.command?.state)
      || ['starting', 'running', 'stopping'].includes(processState);
    if (line.kind === 'tool' && ['completed', 'failed', 'error', 'cancelled'].includes(line.status)
        && !activeCommand && line.part?.tool !== 'question') pending.push(row);
    else { flush(); result.push(row); }
  }
  flush();
  return result;
}
